import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {NotificationManager} from 'react-notifications';
import FileSaver from 'filesaver.js';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import {withRouter} from 'react-router-dom';
import Screen from '../../components/Screen';
import Content from '../../components/Content';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import VirtualizedGrid from '../../components/VirtualizedGrid';
import Form from '../../components/Form';
import Input from '../../components/Input';
import Button from '../../components/Button';
import Menu from '../../components/Menu';
import Icon from '../../components/Icon';
import SideModal from '../../components/SideModal';
import DateTimeInput from '../../components/DateTimeInput';
import { AdminTabs } from './AdminTabs';
import { UserLink } from '../../components/UserLink';
import Answer, {needsCatalogMap} from '../../components/Answer';

//One entry per audit log tab, keyed by the last segment of its route. Everything that used to differ per tab
//lives here so adding a tab means adding an entry rather than another branch in six places.
//
//The key is also what gets handed to the web worker to pick its column set. That worker is served as a static
//file out of /workers instead of being bundled, so it can't import this config — if you add a tab here, add the
//matching key to public/workers/audit-log-worker.js too.
const TABS = {
  answers: {
    label: 'answer changes',
    exportName: 'answer-changes',
    fetchLogs: api.filterAuditLogs,
    //a comment or deletion isn't attached to a question, so only this tab can be filtered by one
    supportsQuestionFilter: true
  },
  deletions: {
    label: 'deleted rows',
    exportName: 'deleted-rows',
    fetchLogs: api.filterDeletedRowsAuditLogs,
    supportsQuestionFilter: false
  },
  comments: {
    label: 'overall comments',
    exportName: 'overall-comments',
    fetchLogs: api.filterRowCommentAuditLogs,
    supportsQuestionFilter: false
  }
};

const DEFAULT_TAB = 'answers';

//archived forms stay in the filter so their history can still be searched, but are marked so they aren't mistaken
//for a live form with the same name
const isArchived = template => template.current === false;
const getTemplateLabel = template => isArchived(template) ? `${template.name} (archived)` : template.name;

//a factory rather than a shared constant, so the tabs never end up holding the same arrays
const emptyTabData = () => ({
  auditLogs: [],
  columns: [],
  data: []
});

const NO_FILTERS = {
  userId: '',
  startDate: null,
  endDate: null,
  templateId: '',
  questionId: ''
};

class AuditLogScreen extends PureComponent {
  state = {
    loading: true,
    error: false,
    users: [],
    templates: [],
    templateNameToId: {},
    questionTextToId: {},
    templateIdToQuestions: {},
    // Store data for each tab separately
    tabData: Object.keys(TABS).reduce((tabData, tab) => ({...tabData, [tab]: emptyTabData()}), {}),
    filters: {...NO_FILTERS},
    applying: false,
    actionsOpen: false,
    filterModalOpen: false,
    exportingData: false,
    // the deleted row whose values are open in the side panel
    deletedValuesCell: null,
    // the setup answers of that row's sheet: null while loading, {failed: true} when they couldn't be loaded
    deletedRowSetup: null,
    // loaded the first time a setup answer needs naming from the catalog
    catalog: null,
    // the Deleted Values sections folded away, kept while moving between rows so a long section stays out of the way
    collapsedDeletedSections: {}
  };

  async componentDidMount() {
    try {
      // Load both users and templates
      const [users, templates] = await Promise.all([
        api.getAllUsers(this.props.credentials),
        api.getCompanyTemplates(this.props.credentials)
      ]);

      // Build the mappings
      const templateNameToId = {};
      const questionTextToId = {};
      const templateIdToQuestions = {};

      templates.forEach(template => {
        templateNameToId[template.name] = template.id;
        templateIdToQuestions[template.id] = [];

        // Process both context questions and regular questions
        [...(template.contextQuestions || []), ...(template.questions || [])].forEach(question => {
          questionTextToId[question.text] = question.id;
          templateIdToQuestions[template.id].push({
            id: question.id,
            text: question.text,
            type: question.type
          });
        });
      });

      this.setState({
        users,
        templates,
        templateNameToId,
        questionTextToId,
        templateIdToQuestions,
        loading: false
      });
    }
    catch (e) {
      this.setState({
        error: true,
        loading: false
      });
      NotificationManager.error('Failed to load data');
    }
  }

  //Resolve the tab from the route. An unrecognised suffix (including the bare /admin and /admin/auditlog paths)
  //falls back to the default rather than silently taking on another tab's endpoint and columns.
  getActiveTab = () => {
    const suffix = this.props.location.pathname.split('/').pop();
    return TABS[suffix] ? suffix : DEFAULT_TAB;
  }

  processAuditLogs = (auditLogs) => {
    const worker = new Worker('/workers/audit-log-worker.js');
    const activeTab = this.getActiveTab();

    worker.onmessage = e => {
      this.setState(prevState => ({
        tabData: {
          ...prevState.tabData,
          [activeTab]: {
            auditLogs,
            columns: e.data.columns,
            data: e.data.data
          }
        },
        loading: false
      }));
    };

    worker.onerror = e => {
      console.error('Worker error:', e);
      this.setState({
        error: true,
        loading: false
      });
    };

    // Pass both auditLogs and activeTab to the worker
    worker.postMessage([auditLogs, activeTab]);
  }

  getAvailableQuestions = () => {
    const {filters, templateIdToQuestions, templates} = this.state;
    
    if (filters.templateId) {
      // If a template is selected, only show questions from that template
      const questions = templateIdToQuestions[filters.templateId] || [];
      return questions.sort((a, b) => a.text.localeCompare(b.text));
    } else {
      // If no template is selected, show all questions from all templates
      const allQuestions = [];
      templates.forEach(template => {
        const questions = [...(template.contextQuestions || []), ...(template.questions || [])];
        questions.forEach(question => {
          // Avoid duplicates
          if (!allQuestions.find(q => q.id === question.id)) {
            allQuestions.push({
              id: question.id,
              text: question.text,
              type: question.type,
              //shown in brackets after the question, so "archived" joins the form name rather than nesting a second pair
              templateName: isArchived(template) ? `${template.name}, archived` : template.name
            });
          }
        });
      });
      return allQuestions.sort((a, b) => a.text.localeCompare(b.text));
    }
  }

  handleFilterChange = (field, value) => {
    this.setState(prevState => {
      const newFilters = {
        ...prevState.filters,
        [field]: value
      };

      // If template changes, clear the question filter since questions are template-specific
      if (field === 'templateId') {
        newFilters.questionId = '';
      }

      return {
        filters: newFilters
      };
    });
  }

  handleApplyFilters = async () => {
    const activeTab = this.getActiveTab();
    const {filters} = this.state;
    
    try {
      this.setState({applying: true});
      
      // Prepare filters for API call
      const apiFilters = {};
      if (filters.userId) apiFilters.userId = filters.userId;
      if (filters.startDate) apiFilters.startDate = filters.startDate.toISOString();
      if (filters.endDate) apiFilters.endDate = filters.endDate.toISOString();
      
      // Send IDs directly to the api instead of names
      if (filters.templateId) {
        apiFilters.templateId = filters.templateId;
      }
      
      //only send the question filter to tabs that can honour it, so a value left over from another tab can't
      //narrow results the user is no longer being shown a question filter for
      if (filters.questionId && TABS[activeTab].supportsQuestionFilter) {
        apiFilters.questionId = filters.questionId;
      }

      const auditLogs = await TABS[activeTab].fetchLogs(apiFilters, this.props.credentials);

      this.processAuditLogs(auditLogs);
      this.setState({
        applying: false,
        filterModalOpen: false // Close the modal after applying
      });
    }
    catch (e) {
      NotificationManager.error('Failed to apply filters');
      this.setState({applying: false});
    }
  }

  handleClearFilters = async () => {
    try {
      this.setState({
        applying: true,
        filters: {...NO_FILTERS}
      });

      // Clear filters - reset only current tab to empty state
      this.setState(prevState => ({
        tabData: {
          ...prevState.tabData,
          [this.getActiveTab()]: emptyTabData()
        },
        applying: false,
        filterModalOpen: false
      }));
    }
    catch (e) {
      NotificationManager.error('Failed to clear filters');
      this.setState({applying: false});
    }
  }

  toggleActions = () => {
    this.setState(prevState => ({
      actionsOpen: !prevState.actionsOpen
    }));
  }

  toggleFilterModal = () => {
    this.setState(prevState => ({
      filterModalOpen: !prevState.filterModalOpen
    }));
  }

  handleExportData = async () => {
    try {
      this.setState({exportingData: true});
      
      const activeTab = this.getActiveTab();
      const {tabData} = this.state;
      const {columns, data} = tabData[activeTab];
      
      if (!data || data.length === 0) {
        NotificationManager.warning('No data to export');
        return;
      }

      // Create CSV content
      const csvContent = this.generateCSV(columns, data);
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `audit-log-${TABS[activeTab].exportName}-export-${timestamp}.csv`;
      
      FileSaver.saveAs(blob, filename);
      NotificationManager.success('Audit log exported successfully');
    }
    catch (e) {
      console.error('Export error:', e);
      NotificationManager.error('Failed to export data');
    }
    finally {
      this.setState({exportingData: false});
    }
  }

  generateCSV = (columns, data) => {
    // Create header row
    const headers = columns.map(col => col.title);
    
    // Convert data rows to CSV format
    const rows = data.map(row => {
      return row.map(cell => {
        let value = '';
        
        switch (cell.type) {
          case 'DATE':
            value = new Date(cell.value).toLocaleString();
            break;
          case 'USER_LINK':
            value = (cell.user && cell.user.userName) || '';
            break;
          case 'FORM_LINK':
            value = cell.value || '';
            break;
          case 'TEXT':
            value = cell.value || '';
            break;
          case 'IMAGE':
            if (!cell.imageId) {
              value = '';
            } else {
              value = cell.imageId;
            }
            break;
          case 'ARRAY':
            if (!cell.values) {
              value = '';
            } else if (Array.isArray(cell.values)) {
              value = cell.values.join('; ');
            } else {
              value = String(cell.values);
            }
            break;
          case 'DELETED_VALUES':
            value = cell.summary || '';
            break;
          default:
            value = '';
        }
        
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (typeof value === 'string') {
          value = value.replace(/"/g, '""'); // Escape quotes
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            value = `"${value}"`; // Wrap in quotes
          }
        }
        
        return value;
      });
    });
    
    // Combine headers and rows
    const csvRows = [headers, ...rows];
    
    // Join with newlines
    return csvRows.map(row => row.join(',')).join('\n');
  }

  handleFormClick = (templateId, sheetId, sheetRowId) => {
    const url = sheetRowId 
      ? `/sheet/${templateId}/${sheetId}/data/${sheetRowId}`
      : `/sheet/${templateId}/${sheetId}/data`;
    
    window.open(url, '_blank');
  }

  renderCell = (cell) => {
    switch (cell.type) {
      case 'DATE':
        return new Date(cell.value).toLocaleString();

      case 'USER_LINK':
        return (cell.user && cell.user.userName) ? (
          <UserLink user={cell.user} />
        ) : '';

      case 'FORM_LINK':
        return cell.value ? (
          <ClickableLink onClick={() => this.handleFormClick(cell.templateId, cell.sheetId, cell.sheetRowId)}>
            {cell.value}
          </ClickableLink>
        ) : '';

      case 'TEXT':
        return cell.value || '';

      case 'IMAGE':
        if (!cell.imageId) {
          return '';
        }
        
        return (
          <ImageThumbnail
            src={`${api.getApiUrl()}/image/${cell.imageId}`}
            alt="Image"
            onClick={() => window.open(`${api.getApiUrl()}/image/${cell.imageId}.jpg`, '_blank')}
            title="Click to view full size"
          />
        );

      case 'ARRAY':
        if (!cell.values) {
          return '';
        }
        
        // Handle case where values might be a string representation of an array
        let displayValue = '';
        if (Array.isArray(cell.values)) {
          displayValue = cell.values.join(', ');
        } else if (typeof cell.values === 'string') {
          displayValue = cell.values;
        } else {
          displayValue = String(cell.values);
        }
        
        return displayValue || '';

      case 'DELETED_VALUES':
        if (!cell.values || cell.values.length === 0) {
          return <MutedText>None recorded</MutedText>;
        }

        return (
          <ClickableLink onClick={() => this.openDeletedValues(cell)}>
            View {cell.values.length} {cell.values.length === 1 ? 'value' : 'values'}
          </ClickableLink>
        );

      default:
        return '';
    }
  }

  //A deleted row only kept its own answers. The setup answers belong to the sheet, which outlives the row, so they are
  //read from the sheet as it is now rather than as it was when the row was deleted.
  openDeletedValues = async (cell) => {
    const template = this.state.templates.find(t => t.id === cell.templateId);
    const setupQuestions = ((template && template.contextQuestions) || [])
      .filter(question => !question.deleted)
      .sort((a, b) => a.order - b.order);

    //kept off state so the check below sees it straight away, rather than once React has applied the update
    this.openDeletedValuesCell = cell;

    //nothing to load for a form without setup questions
    if (setupQuestions.length === 0) {
      this.setState({deletedValuesCell: cell, deletedRowSetup: {values: []}});
      return;
    }

    this.setState({deletedValuesCell: cell, deletedRowSetup: null});

    try {
      const sheet = await api.getSheet(cell.sheetId, this.props.credentials);
      const catalog = !this.state.catalog && setupQuestions.some(needsCatalogMap)
        ? await api.getCatalog(this.props.credentials)
        : this.state.catalog;
      const answers = (sheet && sheet.contextAnswers) || [];

      //the panel may have been closed or moved on to another row while this loaded
      if (this.openDeletedValuesCell !== cell) {
        return;
      }

      this.setState({
        catalog,
        deletedRowSetup: {
          values: setupQuestions.map(question => ({
            question,
            answers: answers.filter(answer => answer.questionId === question.id)
          }))
        }
      });
    }
    catch (e) {
      if (this.openDeletedValuesCell === cell) {
        this.setState({deletedRowSetup: {failed: true}});
      }
    }
  }

  closeDeletedValues = () => {
    this.openDeletedValuesCell = null;
    this.setState({deletedValuesCell: null, deletedRowSetup: null});
  }

  //sits in the panel's header so it stays in view while a long list of values scrolls
  renderDeletedRowSummary = (cell) => (
    <DeletedRowSummary>
      <span><SummaryLabel>Sheet name:</SummaryLabel> {cell.sheetName || 'Unknown'}</span>
      <span><SummaryLabel>Deleted at:</SummaryLabel> {new Date(cell.deletedAt).toLocaleString()}</span>
    </DeletedRowSummary>
  );

  renderDeletedRowSetup = () => {
    const {deletedRowSetup, catalog} = this.state;

    if (!deletedRowSetup) {
      return <Loading message="Loading setup answers..." />;
    }

    if (deletedRowSetup.failed) {
      return <MutedText>The setup answers could not be loaded. The sheet may have been deleted.</MutedText>;
    }

    if (deletedRowSetup.values.length === 0) {
      return <DeletedValuesNote>This form has no setup questions.</DeletedValuesNote>;
    }

    return deletedRowSetup.values.map(({question, answers}) => (
      <DeletedValue key={question.id}>
        <DeletedValueQuestion>{question.text}</DeletedValueQuestion>
        {answers.length > 0
          ? <Answer question={question} answers={answers} catalog={catalog} />
          : <MutedText>No answer</MutedText>}
      </DeletedValue>
    ));
  }

  hasNoSetupQuestions = () => {
    const {deletedRowSetup} = this.state;
    return !!deletedRowSetup && !deletedRowSetup.failed && deletedRowSetup.values.length === 0;
  }

  renderDeletedValues = (cell) => (
    <SideModal.Content>
      {this.renderDeletedValuesSection('setup', 'Setup Questions', (
        <>
          {!this.hasNoSetupQuestions() && (
            <DeletedValuesNote>
              This reflects the current values of the sheet setup
              <br />
              Not the values when the answer row was deleted
            </DeletedValuesNote>
          )}
          {this.renderDeletedRowSetup()}
        </>
      ))}
      {this.renderDeletedValuesSection('row', `Row Questions (${cell.values.length})`, cell.values.map((deletedValue, index) => (
        <DeletedValue key={index}>
          <DeletedValueQuestion>{deletedValue.questionText}</DeletedValueQuestion>
          {deletedValue.imageId ? (
            <ImageThumbnail
              src={`${api.getApiUrl()}/image/${deletedValue.imageId}`}
              alt={deletedValue.questionText}
              onClick={() => window.open(`${api.getApiUrl()}/image/${deletedValue.imageId}.jpg`, '_blank')}
              title="Click to view full size"
            />
          ) : (
            <div>{deletedValue.value || <MutedText>No answer</MutedText>}</div>
          )}
        </DeletedValue>
      )))}
    </SideModal.Content>
  );

  renderDeletedValuesSection = (key, title, content) => {
    const collapsed = !!this.state.collapsedDeletedSections[key];

    return (
      <>
        <DeletedValuesHeading>
          <DeletedValuesToggle type="button" aria-expanded={!collapsed} onClick={() => this.toggleDeletedValuesSection(key)}>
            <Icon name="chevron-right" style={{transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform 0.2s'}} />
            {title}
          </DeletedValuesToggle>
        </DeletedValuesHeading>
        {!collapsed && content}
      </>
    );
  }

  toggleDeletedValuesSection = key => {
    this.setState(prevState => ({
      collapsedDeletedSections: {...prevState.collapsedDeletedSections, [key]: !prevState.collapsedDeletedSections[key]}
    }));
  }

  render() {
    const activeTab = this.getActiveTab();
    const {loading, error, users, filters, applying, actionsOpen, filterModalOpen, exportingData, tabData,
      deletedValuesCell} = this.state;
    // Get data for the current tab
    const {columns, data, auditLogs} = tabData[activeTab];

    if (loading) {
      return (
        <Screen title="Admin" activeLink="admin">
          <AdminTabs />
          <Loading fullScreen message="Loading audit logs..." />
        </Screen>
      );
    }

    if (error) {
      return (
        <Screen title="Admin" activeLink="admin">
          <AdminTabs />
          <Error fullScreen message="Failed to load audit logs" />
        </Screen>
      );
    }

    return (
      <Screen 
        title="Admin"
        activeLink="admin"
        actions={[
          <Button id="audit-actions" onClick={this.toggleActions}>
            Options <Icon name="chevron-down" />
          </Button>
        ]}>
        <AdminTabs />
        <Content>
          <GridContainer>
            {columns.length > 0 && data.length > 0 ? (
              <Grid
                key={`audit-grid-${data.length}`}
                columns={columns}
                data={data}
                children={this.renderCell}
              />
            ) : (
              <EmptyStateContainer>
                {applying || (auditLogs.length > 0 && data.length === 0) ? (
                  <Loading message="Loading audit logs..." />
                ) : (
                  <EmptyStateMessage>
                    {auditLogs.length === 0 ?
                      `Use filters to display relevant ${TABS[activeTab].label}` :
                      `No ${TABS[activeTab].label} match your current filters`}
                  </EmptyStateMessage>
                )}
              </EmptyStateContainer>
            )}
          </GridContainer>
        </Content>

        <Menu
          open={actionsOpen} 
          position="bottom-end"  
          offsetY={5} 
          relativeTo="audit-actions" 
          onClose={this.toggleActions}>
          <Menu.Section>
            <Menu.Text>Filter Options</Menu.Text>
            <Menu.Divider />
            <Menu.Option onClick={this.toggleFilterModal}>Filter data</Menu.Option>
          </Menu.Section>
          <Menu.Section>
            <Menu.Text>Export Options</Menu.Text>
            <Menu.Divider />
            <Menu.Option disabled={exportingData || data.length === 0} onClick={this.handleExportData}>
              {exportingData ? 'Exporting...' : `Export data (${data.length} records)`}
            </Menu.Option>
          </Menu.Section>
        </Menu>

        <Form onSubmit={this.handleApplyFilters}>
          <SideModal
            title="Filter Data"
            open={filterModalOpen}
            onClose={this.toggleFilterModal}
            options={[
              <Button type="submit" disabled={applying}>
                {applying ? 'Applying...' : 'Apply Filters'}
              </Button>,
              <Button secondary onClick={this.handleClearFilters} disabled={applying}>
                Clear Filters
              </Button>
            ]}>
            <SideModal.Content>
              <Form.Group>
                <Input
                  id="user-filter"
                  label="User"
                  type="select"
                  value={filters.userId}
                  onChange={e => this.handleFilterChange('userId', e.target.value)}
                >
                  <option value="">All Users</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.userName}
                    </option>
                  ))}
                </Input>
              </Form.Group>
              <Form.Group>
                <DateTimeInput
                  id="start-date-filter"
                  label="Start Date & Time"
                  placeholder="Select start date and time"
                  date={filters.startDate}
                  onChange={date => this.handleFilterChange('startDate', date)}
                />
              </Form.Group>
              <Form.Group>
                <DateTimeInput
                  id="end-date-filter"
                  label="End Date & Time"
                  placeholder="Select end date and time"
                  date={filters.endDate}
                  onChange={date => this.handleFilterChange('endDate', date)}
                />
              </Form.Group>
              <Form.Group>
                <Input
                  id="template-id-filter"
                  label="Template"
                  type="select"
                  value={filters.templateId}
                  onChange={e => this.handleFilterChange('templateId', e.target.value)}
                >
                  <option value="">All Templates</option>
                  {this.state.templates
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(template => (
                      <option key={template.id} value={template.id}>
                        {getTemplateLabel(template)}
                      </option>
                    ))}
                </Input>
              </Form.Group>
              {TABS[activeTab].supportsQuestionFilter && (
                <Form.Group>
                  <Input
                    id="question-id-filter"
                    label="Question"
                    type="select"
                    value={filters.questionId}
                    onChange={e => this.handleFilterChange('questionId', e.target.value)}
                  >
                    <option value="">All Questions</option>
                    {this.getAvailableQuestions().map(question => (
                      <option key={question.id} value={question.id}>
                        {filters.templateId ? question.text : `${question.text} (${question.templateName})`}
                      </option>
                    ))}
                  </Input>
                </Form.Group>
              )}
            </SideModal.Content>
          </SideModal>
        </Form>

        <SideModal
          title="Deleted Values"
          header={deletedValuesCell && this.renderDeletedRowSummary(deletedValuesCell)}
          open={!!deletedValuesCell}
          onClose={this.closeDeletedValues}>
          {deletedValuesCell && this.renderDeletedValues(deletedValuesCell)}
        </SideModal>
      </Screen>
    );
  }
}

const GridContainer = styled.div`
  height: calc(100vh - 23rem);
  min-height: 30rem;
  
  @media (max-width: 50rem) {
    height: calc(100vh - 21rem);
    min-height: 25rem;
  }
`;

const Grid = styled(VirtualizedGrid)`
  height: 100%;
`;

const ClickableLink = styled.span`
  color: #0049ee;
  cursor: pointer;
  user-select: text;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ImageThumbnail = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 0.25rem;
  cursor: pointer;
  border: 1px solid #e4e4e4;
  
  &:hover {
    border-color: #0049ee;
    box-shadow: 0 2px 4px rgba(0, 73, 238, 0.1);
  }
`;

const MutedText = styled.span`
  color: #666;
`;

//the two fields sit side by side, and wrap onto their own lines when the panel is narrow
const DeletedRowSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 2rem;
`;

const SummaryLabel = styled.span`
  color: #666;
`;

const DeletedValue = styled.div`
  padding: 0.75rem 0;
  border-bottom: 1px solid #e4e4e4;
  overflow-wrap: anywhere;

  &:last-child {
    border-bottom: none;
  }
`;

const DeletedValuesHeading = styled.h4`
  margin: 1.5rem 0 0.25rem;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #333;

  &:first-child {
    margin-top: 0;
  }
`;

const DeletedValuesToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
`;

const DeletedValuesNote = styled.p`
  margin: 0 0 0.25rem;
  color: #666;
  font-size: 0.875rem;
`;

const DeletedValueQuestion = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 20rem;
`;

const EmptyStateMessage = styled.div`
  text-align: center;
  color: #666;
  font-size: 1.1rem;
  padding: 2rem;
`;

export default withRouter(withUser(AuditLogScreen)); 